/**
 * EventForm.tsx
 *
 * Shared two-column form for creating/editing single and external events.
 * All state is local — no API calls.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  FiMapPin,
  FiCalendar,
  FiCamera,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiTrash2,
  FiX,
  FiCheck,
  FiPlus,
  FiMusic,
  FiSearch,
  FiTag,
  FiInfo,
  FiLock,
  FiUpload,
  FiClock,
} from 'react-icons/fi';
import { Button } from '../ui/Button';
import { FormLabel } from '../ui/FormLabel';
import { Input } from '../ui/Input';
import { DateInput } from '../ui/DateInput';
import { TimezoneSelect, DEFAULT_TIMEZONE } from '../ui/TimezoneSelect';
import { CollaboratorsSelector } from '../media/CollaboratorsSelector';
import { LocationSearchInput } from '../common/LocationSearchInput';
import { TicketForm } from './TicketForm';
import { toast } from '../../utils/toast';
import { UPLOAD_MAX_FILE_SIZE_BYTES } from '../../utils/uploadValidation';
import type { LocationBody } from '../../services/locationService';
import { useCheckUsernameAvailability } from '../../api/events';
import { useGetGenresQuery, useCreateGenreMutation } from '../../api/genres';
import { useAuth } from '../../hooks/useAuth';
import { isValidGuestEmail, hasInvalidGuestEmail } from '../../utils/guestInvite';
import { isTicketExpired, getEventDurationHours } from '../../utils/ticketExpiry';
import { normalizeGenreKey, buildGenreIdMap, buildGenreNameMapById } from '../../utils/genre';
import { useDominantColor } from '../../hooks/useDominantColor';
import { normalizeColor } from '../../utils/dominantColor';
import { useEventAccentColor } from '../../hooks/useEventAccentColor';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EventFormValues {
  event_title: string;
  info: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  timezone: string;
  locationBody: LocationBody | null;
  locationDetails?: string;
  event_age: string;
  genres: number[];
  images: string[];
  /** New local files (parallel to images array; null = existing server URL) */
  imageFiles: (File | null)[];
  /** Server image IDs (parallel to images array; null = new local file) */
  existingImageIds: (number | null)[];
  tickets?: Array<{
    id?: number;
    ticket_type: string;
    price: number;
    quantity: number;
    ticket_number_issued?: number;
    remaining_tickets?: number;
    description?: string;
    age_requirement?: string;
    sales_start_date?: string;
    sales_start_time?: string;
    sales_end_date?: string;
    sales_end_time?: string;
    has_end_purchase_period?: boolean;
    go_live_on_publish?: boolean;
    sub_events?: boolean;
    multi_day_access?: boolean;
  }>;
  is_private?: boolean;
  guests?: { email: string }[];
  collaboratorIds: number[];
  is_draft_or_published: 'draft' | 'published';
  deletedImageIds?: number[];
  /** Dominant color of the first/primary image, as a "#rrggbb" hex string. */
  dominantColor?: string | null;
}

interface EventFormProps {
  mode: 'create' | 'edit';
  title: string;
  statusBadge?: 'draft' | 'published';
  secondaryActionLabel?: string;
  onSubmit: (values: EventFormValues, action: 'save' | 'publish') => void;
  onSecondaryAction?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  initialData?: Partial<EventFormValues>;
  isLoading?: boolean;
  /**
   * Whether at least one ticket type must be added before saving/publishing.
   * Defaults to true (standalone single events). Multi-event parent events and
   * sub-events don't require tickets on every event in the family, so callers
   * editing those should pass `false`.
   */
  ticketsRequired?: boolean;
  /** Hide the private-event toggle for event types that shouldn't expose it. */
  hidePrivateToggle?: boolean;
}

const MAX_IMAGES = 5;
const MAX_TICKETS = 10;

const AGE_OPTIONS = ['18+', '21+', 'All Ages'];

export const AGE_WIRE_MAP: Record<string, string> = {
  'All Ages': 'all_ages',
  '13+': '13',
  '18+': '18',
  '21+': '21',
};

export const WIRE_TO_AGE_MAP: Record<string, string> = {
  all_ages: 'All Ages',
  '13+': '13+',
  '18+': '18+',
  '21+': '21+',
  // Backward compatibility with numeric wire values
  '13': '13+',
  '18': '18+',
  '21': '21+',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const EventForm: React.FC<EventFormProps> = ({
  mode,
  title,
  statusBadge,
  secondaryActionLabel,
  onSubmit,
  onSecondaryAction,
  onDirtyChange,
  initialData,
  isLoading = false,
  ticketsRequired = true,
  hidePrivateToggle = false,
}) => {
  const { user } = useAuth();

  // ── Image state ──
  const [images, setImages] = useState<string[]>(initialData?.images ?? []);
  const [imageFiles, setImageFiles] = useState<(File | null)[]>(initialData?.imageFiles ?? []);
  const [existingImageIds, setExistingImageIds] = useState<(number | null)[]>(
    initialData?.existingImageIds ?? [],
  );
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dominantColor] = useDominantColor(images[0], initialData?.dominantColor ?? null);
  const eventAccent = useEventAccentColor(dominantColor);

  // ── Basic info ──
  const [eventTitle, setEventTitle] = useState(initialData?.event_title ?? '');
  const [info, setInfo] = useState(initialData?.info ?? '');
  const [eventAge, setEventAge] = useState(initialData?.event_age ?? '18+');

  // ── Date & Time ──
  const [startDate, setStartDate] = useState(initialData?.startDate ?? '');
  const [startTime, setStartTime] = useState(initialData?.startTime ?? '');
  const [endDate, setEndDate] = useState(initialData?.endDate ?? '');
  const [endTime, setEndTime] = useState(initialData?.endTime ?? '');
  const [timezone, setTimezone] = useState(initialData?.timezone ?? DEFAULT_TIMEZONE);

  // ── Location ──
  const [locationBody, setLocationBody] = useState<LocationBody | null>(
    initialData?.locationBody ??
      ((initialData as any)?.venue
        ? ({
            address: (initialData as any).venue,
            city: (initialData as any).city ?? '',
            state: (initialData as any).state ?? '',
            country: (initialData as any).country ?? '',
            latitude: 0,
            longitude: 0,
          } as LocationBody)
        : null),
  );
  const [locationDetails, setLocationDetails] = useState(initialData?.locationDetails ?? '');
  const [showLocationModal, setShowLocationModal] = useState(false);

  // ── Genres ──
  const queryClient = useQueryClient();
  const [genres, setGenres] = useState<string[]>([]);
  const [showGenreModal, setShowGenreModal] = useState(false);
  const [genreSearch, setGenreSearch] = useState('');
  const [customGenres, setCustomGenres] = useState<string[]>([]);
  const { data: genresData, isLoading: isLoadingGenres } = useGetGenresQuery();
  const createGenreMutation = useCreateGenreMutation();
  const apiGenreRows = useMemo(
    () => (Array.isArray(genresData?.data) ? (genresData?.data ?? []) : []),
    [genresData],
  );
  const apiGenres = useMemo(() => apiGenreRows.map((genre) => genre.name), [apiGenreRows]);
  const apiGenreIdMap = useMemo(() => buildGenreIdMap(apiGenreRows), [apiGenreRows]);
  const apiGenreNameById = useMemo(() => buildGenreNameMapById(apiGenreRows), [apiGenreRows]);
  const customGenreIdMap = useRef<Map<string, number>>(new Map());
  const didResolveInitialGenres = useRef(false);
  useEffect(() => {
    if (didResolveInitialGenres.current || isLoadingGenres) return;
    didResolveInitialGenres.current = true;
    if (!initialData?.genres?.length) return;
    const resolved = initialData.genres
      .map((id) => apiGenreNameById.get(id))
      .filter((name): name is string => Boolean(name));
    setGenres(resolved);
  }, [isLoadingGenres, apiGenreNameById, initialData?.genres]);
  const availableGenres = useMemo(
    () => [...customGenres.filter((g) => !apiGenres.includes(g)), ...apiGenres],
    [customGenres, apiGenres],
  );
  const filteredGenres = useMemo(() => {
    const search = genreSearch.toLowerCase().trim();
    return [...availableGenres]
      .sort((a, b) => a.localeCompare(b))
      .filter((g) => g.toLowerCase().includes(search));
  }, [availableGenres, genreSearch]);

  const resolveGenreIds = useCallback((): number[] => {
    return Array.from(
      new Set(
        genres
          .map((genre) => genre.trim())
          .filter(Boolean)
          .map((genre) => {
            const key = normalizeGenreKey(genre);
            return apiGenreIdMap.get(key) ?? customGenreIdMap.current.get(key);
          })
          .filter((id): id is number => typeof id === 'number'),
      ),
    );
  }, [genres, apiGenreIdMap]);

  const handleAddGenre = async () => {
    const name = genreSearch.trim();
    if (!name) return;
    if (availableGenres.some((g) => g.toLowerCase() === name.toLowerCase())) {
      toast.error('Genre already exists.');
      return;
    }
    try {
      const created = await createGenreMutation.mutateAsync(name);
      const createdName = created?.name || name;
      if (typeof created?.id === 'number') {
        customGenreIdMap.current.set(normalizeGenreKey(createdName), created.id);
        if (typeof created.slug === 'string' && created.slug.trim()) {
          customGenreIdMap.current.set(normalizeGenreKey(created.slug), created.id);
        }
      }
      setCustomGenres((prev) => (prev.includes(createdName) ? prev : [...prev, createdName]));
      setGenres((prev) => (prev.includes(createdName) ? prev : [...prev, createdName]));
      setGenreSearch('');
      await queryClient.invalidateQueries({ queryKey: ['genres'] });
      toast.success('Genre added.');
    } catch (error) {
      console.error('Create genre failed:', error);
      toast.error('Failed to create genre. Try again.');
    }
  };

  // ── Tickets (single only) ──
  const [tickets, setTickets] = useState(initialData?.tickets ?? []);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketFormMode, setTicketFormMode] = useState<'list' | 'add' | 'edit'>('list');
  const [editingTicketIndex, setEditingTicketIndex] = useState<number | null>(null);

  const [isPrivate, setIsPrivate] = useState(initialData?.is_private ?? false);
  const [guests, setGuests] = useState<{ email: string }[]>(initialData?.guests ?? []);
  const [guestInput, setGuestInput] = useState('');
  const [isValidatingUsernames, setIsValidatingUsernames] = useState(false);
  const checkUsernameAvailability = useCheckUsernameAvailability();

  const isPublishedPrivate =
    initialData?.is_private && mode === 'edit' && statusBadge === 'published';

  // ── Collaborators ──
  const [collaboratorIds, setCollaboratorIds] = useState<number[]>(
    initialData?.collaboratorIds ?? [],
  );

  // ── Validation ──
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Dirty tracking ──
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!initialData) {
      setIsDirty(false);
      onDirtyChange?.(false);
      return;
    }
    const current: Partial<EventFormValues> = {
      event_title: eventTitle,
      info,
      startDate,
      startTime,
      endDate,
      endTime,
      timezone,
      locationBody,
      locationDetails,
      event_age: eventAge,
      genres: [...resolveGenreIds()].sort((a, b) => a - b),
      images,
      imageFiles,
      existingImageIds,
      tickets,
      is_private: isPrivate,
      guests,
      collaboratorIds,
      dominantColor: normalizeColor(dominantColor),
    };
    // Mirror the same defaults each useState above was seeded with, so a freshly
    // mounted form (no user edits yet) always compares equal to `current`.
    const initialSnapshot: Partial<EventFormValues> = {
      event_title: initialData.event_title ?? '',
      info: initialData.info ?? '',
      startDate: initialData.startDate ?? '',
      startTime: initialData.startTime ?? '',
      endDate: initialData.endDate ?? '',
      endTime: initialData.endTime ?? '',
      timezone: initialData.timezone ?? DEFAULT_TIMEZONE,
      locationBody: initialData.locationBody ?? null,
      locationDetails: initialData.locationDetails ?? '',
      event_age: initialData.event_age ?? '18+',
      genres: [...(initialData.genres ?? [])].sort((a, b) => a - b),
      images: initialData.images ?? [],
      imageFiles: initialData.imageFiles ?? [],
      existingImageIds: initialData.existingImageIds ?? [],
      tickets: initialData.tickets ?? [],
      is_private: initialData.is_private ?? false,
      guests: initialData.guests ?? [],
      collaboratorIds: initialData.collaboratorIds ?? [],
      dominantColor: normalizeColor(initialData.dominantColor ?? null),
    };
    const dirty = JSON.stringify(current) !== JSON.stringify(initialSnapshot);
    setIsDirty(dirty);
    onDirtyChange?.(dirty);
  }, [
    eventTitle,
    info,
    startDate,
    startTime,
    endDate,
    endTime,
    timezone,
    locationBody,
    locationDetails,
    eventAge,
    resolveGenreIds,
    images,
    imageFiles,
    existingImageIds,
    tickets,
    isPrivate,
    guests,
    collaboratorIds,
    dominantColor,
    initialData,
    onDirtyChange,
  ]);

  // ── Image handlers ──
  const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];

  const handleAddImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.warn('Only the first 5 images will be processed.');
      return;
    }
    const toAdd = Array.from(files).slice(0, remaining);
    if (files.length > remaining) {
      toast.warn('Only the first 5 images will be processed.');
    }
    const invalidFile = toAdd.find((f) => !validImageTypes.includes(f.type));
    if (invalidFile) {
      toast.error('Unsupported image format.');
      e.target.value = '';
      return;
    }
    const oversizedFile = toAdd.find((f) => f.size > UPLOAD_MAX_FILE_SIZE_BYTES);
    if (oversizedFile) {
      toast.error('Image size must not exceed 10MB.');
      e.target.value = '';
      return;
    }
    const newUrls = toAdd.map((f) => URL.createObjectURL(f));
    setImages((prev) => {
      const next = [...prev, ...newUrls];
      setSelectedImageIndex((cur) => (cur >= next.length ? 0 : cur));
      return next;
    });
    setImageFiles((prev) => [...prev, ...toAdd]);
    setExistingImageIds((prev) => [...prev, ...toAdd.map(() => null)]);
    if (errors.images)
      setErrors((prev) => {
        const next = { ...prev };
        delete next.images;
        return next;
      });
  };

  const removeImage = (idx: number) => {
    const existingId = existingImageIds[idx];
    if (existingId != null) {
      setDeletedImageIds((prev) => [...prev, existingId]);
    }
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setExistingImageIds((prev) => prev.filter((_, i) => i !== idx));
    setSelectedImageIndex((cur) => {
      if (idx === cur) return 0;
      if (idx < cur) return cur - 1;
      return cur;
    });
  };

  const moveImage = (idx: number, dir: 'left' | 'right') => {
    const swapArr = <T,>(arr: T[]): T[] => {
      const next = [...arr];
      const swapIdx = dir === 'left' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= next.length) return arr;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    };
    setImages((prev) => swapArr(prev));
    setImageFiles((prev) => swapArr(prev));
    setExistingImageIds((prev) => swapArr(prev));
    setSelectedImageIndex((cur) => {
      if (cur === idx) return dir === 'left' ? idx - 1 : idx + 1;
      return cur;
    });
  };

  // ── Guest invite validation ──
  const isValidEmailFormat = isValidGuestEmail;
  const isValidUsernameFormat = (value: string) => /^@[a-zA-Z0-9._]{3,30}$/.test(value);

  const checkUsernameExists = async (username: string): Promise<boolean> => {
    try {
      const result = await checkUsernameAvailability.mutateAsync(username);
      // available === true  → username does NOT exist → reject
      // available === false → username exists → accept
      return !result.available;
    } catch {
      // On API failure, conservatively reject to avoid inviting an invalid username
      return false;
    }
  };

  const addGuestToken = async (token: string): Promise<boolean> => {
    if (!token) return false;
    if (user) {
      if (
        token.toLowerCase() === user.email.toLowerCase() ||
        token.toLowerCase() === `@${user.username?.toLowerCase()}`
      ) {
        toast.error('You cannot invite yourself.');
        return false;
      }
    }
    if (token.startsWith('@')) {
      if (!isValidUsernameFormat(token)) {
        toast.error('Enter a valid @username (3–30 characters, letters, numbers, . or _).');
        return false;
      }
      const username = token.slice(1);
      setIsValidatingUsernames(true);
      try {
        const exists = await checkUsernameExists(username);
        if (!exists) {
          toast.error('User not found. Please enter a registered username.');
          return false;
        }
      } finally {
        setIsValidatingUsernames(false);
      }
    } else {
      if (!isValidEmailFormat(token)) {
        toast.error('Enter a valid email or username.');
        return false;
      }
    }
    setGuests((prev) => {
      if (prev.some((g) => g.email.toLowerCase() === token.toLowerCase())) return prev;
      return [...prev, { email: token }];
    });
    return true;
  };

  const addGuestTokens = async (tokens: string[]): Promise<string[]> => {
    let filteredTokens = [...tokens];
    if (user) {
      const selfInviteTokens = filteredTokens.filter(
        (t) =>
          t.toLowerCase() === user.email.toLowerCase() ||
          t.toLowerCase() === `@${user.username?.toLowerCase()}`,
      );
      if (selfInviteTokens.length > 0) {
        toast.error('You cannot invite yourself.');
        filteredTokens = filteredTokens.filter(
          (t) =>
            t.toLowerCase() !== user.email.toLowerCase() &&
            t.toLowerCase() !== `@${user.username?.toLowerCase()}`,
        );
      }
    }
    const accepted: string[] = [];
    const usernames = filteredTokens.filter((t) => t.startsWith('@'));
    if (usernames.length > 0) {
      setIsValidatingUsernames(true);
      try {
        for (const token of usernames) {
          if (!isValidUsernameFormat(token)) {
            toast.error(`"${token}" is not a valid @username.`);
            continue;
          }
          const exists = await checkUsernameExists(token.slice(1));
          if (!exists) {
            toast.error(`"${token}" not found. Please enter a registered username.`);
            continue;
          }
          accepted.push(token);
        }
      } finally {
        setIsValidatingUsernames(false);
      }
    }
    for (const token of filteredTokens) {
      if (token.startsWith('@')) continue;
      if (!isValidEmailFormat(token)) {
        toast.error(`"${token}" is not a valid email address.`);
        continue;
      }
      accepted.push(token);
    }
    if (accepted.length > 0) {
      setGuests((prev) => {
        const existing = new Set(prev.map((g) => g.email.toLowerCase()));
        const unique: string[] = [];
        for (const email of accepted) {
          const key = email.toLowerCase();
          if (existing.has(key)) continue;
          existing.add(key);
          unique.push(email);
        }
        return [...prev, ...unique.map((email) => ({ email }))];
      });
    }
    return accepted;
  };

  // ── Validation ──
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!eventTitle.trim()) newErrors.eventTitle = 'Event title is required';
    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      if (mode !== 'edit' && startDate < todayStr) {
        newErrors.startDate = 'Start date cannot be in the past.';
      }
    }
    if (!startTime) newErrors.startTime = 'Start time is required';
    if (!endDate) {
      newErrors.endDate = 'End date is required';
    } else if (startDate && endDate < startDate) {
      newErrors.endDate = 'End date cannot be before start date.';
    } else if (startDate && endDate === startDate && startTime && endTime && endTime <= startTime) {
      newErrors.endTime = 'End time must be after start time.';
    }
    if (!endTime) newErrors.endTime = 'End time is required';
    if (!locationBody) newErrors.location = 'Event address is required';
    if (genres.length === 0) newErrors.genres = 'At least one genre is required';
    if (tickets.length === 0) {
      if (ticketsRequired) {
        newErrors.tickets = 'At least one ticket type is required';
      }
    } else {
      if (tickets.length > 10) {
        newErrors.tickets = 'Maximum of 10 ticket types allowed.';
      }
      const names = tickets.map((t) => t.ticket_type.trim().toLowerCase()).filter(Boolean);
      const uniqueNames = new Set(names);
      if (names.length !== uniqueNames.size) {
        newErrors.tickets = 'Ticket names must be unique.';
      }
      // Check if event duration allows multi-day access tickets
      const durationHours = getEventDurationHours(startDate, startTime, endDate, endTime);
      const hasMultiDayTicket = tickets.some((t) => t.multi_day_access);
      if (hasMultiDayTicket && durationHours <= 12) {
        newErrors.tickets = 'Multi-day access is only allowed for events longer than 12 hours.';
      }
    }

    // At least one image is required
    if (images.length < 1) {
      newErrors.images = 'Please upload at least one image.';
    }

    if (isPrivate && hasInvalidGuestEmail(guests)) {
      newErrors.guests = 'Remove or fix invalid guest email addresses before continuing.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (action: 'save' | 'publish') => {
    if (mode === 'edit' && action === 'save' && !isDirty) {
      // Nothing to save — prevent unnecessary API request.
      return;
    }
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }
    const values: EventFormValues = {
      event_title: eventTitle,
      info,
      startDate,
      startTime,
      endDate,
      endTime,
      timezone,
      locationBody,
      locationDetails,
      event_age: eventAge,
      genres: resolveGenreIds(),
      images,
      imageFiles,
      existingImageIds,
      tickets,
      is_private: isPrivate,
      guests,
      collaboratorIds,
      is_draft_or_published: action === 'publish' ? 'published' : 'draft',
      deletedImageIds,
      dominantColor,
    };
    onSubmit(values, action);
  };

  // ── Render helpers ──
  const inputClass =
    'w-full bg-zinc-900/60 border border-white/8 rounded-2xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-all text-[14px]';

  return (
    <div
      className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8"
      style={eventAccent.style}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleAddImages}
      />

      {/* ── Title row ── */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-white text-2xl sm:text-[32px] font-bold">{title}</h1>
          {statusBadge && (
            <span
              className={`px-3 py-1 rounded-full text-[12px] font-semibold ${
                statusBadge === 'published'
                  ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                  : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
              }`}
            >
              {statusBadge === 'published' ? 'Published' : 'Draft'}
            </span>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-3">
          {mode === 'edit' && secondaryActionLabel && onSecondaryAction && (
            <Button
              type="button"
              variant="outline"
              className="rounded-full px-6 border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={onSecondaryAction}
            >
              <FiX size={15} className="mr-1.5" />
              {secondaryActionLabel}
            </Button>
          )}
          {mode === 'create' && (
            <>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full px-6 border border-white/10 text-zinc-300 hover:bg-white/5"
                onClick={() => handleSubmit('save')}
                disabled={isLoading}
              >
                <FiCheck size={15} className="mr-1.5" />
                Save as Draft
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="rounded-full px-6 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
                onClick={() => handleSubmit('publish')}
                disabled={isLoading}
              >
                <FiPlus size={15} className="mr-1.5" />
                Create
              </Button>
            </>
          )}
          {mode === 'edit' && statusBadge === 'draft' && (
            <>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full px-6 border border-white/10 text-zinc-300 hover:bg-white/5"
                onClick={() => handleSubmit('save')}
                disabled={isLoading || !isDirty}
              >
                <FiCheck size={15} className="mr-1.5" />
                Save as Draft
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="rounded-full px-6 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
                onClick={() => handleSubmit('publish')}
                disabled={isLoading}
              >
                <FiPlus size={15} className="mr-1.5" />
                Publish
              </Button>
            </>
          )}
          {mode === 'edit' && statusBadge !== 'draft' && (
            <Button
              type="button"
              variant="secondary"
              className="rounded-full px-6 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 disabled:bg-zinc-600 disabled:text-zinc-400"
              onClick={() => handleSubmit('save')}
              disabled={isLoading || !isDirty}
            >
              <FiCheck size={15} className="mr-1.5" />
              Save Changes
            </Button>
          )}
        </div>
      </div>

      {/* ── Subtitle ── */}
      <p className="text-white text-base font-semibold mb-1">Create an unforgettable event</p>
      <p className="text-zinc-500 text-sm mb-8">
        Fill in the details to share your event with the world
      </p>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* ── LEFT COLUMN ── */}
        <div className="space-y-8">
          {/* ── Image Upload ── */}
          <div>
            <FormLabel required className="mb-2">
              Event Images
            </FormLabel>
            <div
              className="relative w-full aspect-[16/9] rounded-2xl border-2 border-dashed border-white/10 bg-zinc-900/40 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-white/5 hover:border-white/20 transition-all overflow-hidden"
              onClick={() => images.length < MAX_IMAGES && fileInputRef.current?.click()}
            >
              {images.length > 0 ? (
                <img
                  src={images[selectedImageIndex] ?? images[0]}
                  alt="Cover"
                  className="w-full h-full object-contain"
                />
              ) : (
                <>
                  <FiCamera size={28} className="text-zinc-600" />
                  <p className="text-zinc-500 text-sm font-medium">Add Event Images</p>
                  <p className="text-zinc-600 text-xs">Up to 5 Photos</p>
                </>
              )}
            </div>

            {/* Attached photos strip */}
            {images.length > 0 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {images.map((src, i) => (
                  <div
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedImageIndex(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedImageIndex(i);
                      }
                    }}
                    className={`relative w-20 h-20 rounded-xl overflow-hidden shrink-0 group cursor-pointer transition-all ${
                      i === selectedImageIndex
                        ? 'ring-2 ring-white border border-transparent'
                        : 'border border-white/10 hover:border-white/30'
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(i);
                      }}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-red-500 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                    >
                      <FiTrash2 size={10} />
                    </button>
                    {images.length > 1 && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveImage(i, 'left');
                          }}
                          disabled={i === 0}
                          className="w-4 h-4 rounded-full bg-black/60 flex items-center justify-center text-white disabled:opacity-30"
                        >
                          <FiChevronLeft size={8} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveImage(i, 'right');
                          }}
                          disabled={i === images.length - 1}
                          className="w-4 h-4 rounded-full bg-black/60 flex items-center justify-center text-white disabled:opacity-30"
                        >
                          <FiChevronRight size={8} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {images.length < MAX_IMAGES && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center text-zinc-600 hover:text-zinc-400 hover:border-white/20 transition-colors shrink-0"
                  >
                    <FiPlus size={18} />
                  </button>
                )}
              </div>
            )}
            <p className="text-zinc-600 text-xs mt-1.5">Attached photos</p>
            {errors.images && <p className="text-red-400 text-xs mt-1">{errors.images}</p>}
          </div>

          {/* ── Basic Information ── */}
          <div className="space-y-6">
            <h2 className="text-white text-lg font-semibold">Basic Information</h2>

            {/* Event Title */}
            <div>
              <FormLabel required>Event Title</FormLabel>
              <input
                type="text"
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                placeholder="Enter Event Title"
                className={`${inputClass} mt-2`}
              />
              {errors.eventTitle && (
                <p className="text-red-400 text-xs mt-1">{errors.eventTitle}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <FormLabel>Describe your event (Optional)</FormLabel>
              <textarea
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                placeholder="Event description"
                rows={5}
                className={`${inputClass} mt-2 resize-none`}
              />
              {errors.info && <p className="text-red-400 text-xs mt-1">{errors.info}</p>}
            </div>

            {/* Age Requirement */}
            <div>
              <FormLabel>Age Requirement</FormLabel>
              <div className="relative mt-2">
                <select
                  value={eventAge}
                  onChange={(e) => setEventAge(e.target.value)}
                  className={`${inputClass} appearance-none cursor-pointer pr-10`}
                >
                  {AGE_OPTIONS.map((age) => (
                    <option key={age} value={age} className="bg-zinc-900 text-white">
                      {age}
                    </option>
                  ))}
                </select>
                <FiChevronDown
                  size={16}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="space-y-8">
          {/* ── Date & Time ── */}
          <div className="space-y-5">
            <h2 className="text-white text-lg font-semibold">Date & Time</h2>

            {/* Start Date / End Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel required>Start Date</FormLabel>
                <DateInput
                  value={startDate}
                  onChange={(value) => setStartDate(value)}
                  className={`h-14 bg-zinc-900/60 ${errors.startDate ? 'border-red-500/60' : 'border-white/8'}`}
                />
                {errors.startDate && (
                  <p className="text-red-400 text-xs mt-1">{errors.startDate}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel required>End Date</FormLabel>
                <DateInput
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  className={`h-14 bg-zinc-900/60 ${errors.endDate ? 'border-red-500/60' : 'border-white/8'}`}
                />
                {errors.endDate && <p className="text-red-400 text-xs mt-1">{errors.endDate}</p>}
              </div>
            </div>

            {/* Start Time / End Time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel required>Start Time</FormLabel>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  placeholder="Start time"
                  icon={FiClock}
                  className="h-14 bg-zinc-900/60 border-white/8 scheme-dark"
                />
                {errors.startTime && (
                  <p className="text-red-400 text-xs mt-1">{errors.startTime}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel required>End Time</FormLabel>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  placeholder="End time"
                  icon={FiClock}
                  className="h-14 bg-zinc-900/60 border-white/8 scheme-dark"
                />
                {errors.endTime && <p className="text-red-400 text-xs mt-1">{errors.endTime}</p>}
              </div>
            </div>

            {/* Timezone */}
            <div className="flex flex-col gap-2">
              <FormLabel required>Timezone</FormLabel>
              <TimezoneSelect value={timezone} onChange={setTimezone} />
            </div>

            {/* Location */}
            <div className="space-y-5">
              <h2 className="text-white text-lg font-semibold">Location</h2>

              {/* Event Address */}
              <div>
                <FormLabel required>Event Address</FormLabel>
                <button
                  type="button"
                  onClick={() => setShowLocationModal(true)}
                  className={`w-full flex items-center gap-3 mt-2 px-4 py-3.5 rounded-2xl border transition-all text-left ${
                    locationBody
                      ? 'bg-zinc-900/60 border-white/8 text-white'
                      : 'bg-zinc-900/60 border-white/8 text-zinc-600'
                  }`}
                >
                  <FiMapPin
                    size={18}
                    className={locationBody ? 'text-blue-400' : 'text-zinc-500'}
                  />
                  <span className="text-[14px] truncate">
                    {locationBody
                      ? [locationBody.city, locationBody.state, locationBody.country]
                          .filter(Boolean)
                          .join(', ') || locationBody.address
                      : 'Select location'}
                  </span>
                </button>
                {locationBody && (
                  <p className="text-zinc-600 text-xs mt-1.5 text-right">
                    {
                      (
                        [locationBody.city, locationBody.state, locationBody.country]
                          .filter(Boolean)
                          .join(', ') || locationBody.address
                      ).length
                    }
                    /200
                  </p>
                )}
                {errors.location && <p className="text-red-400 text-xs mt-1">{errors.location}</p>}
              </div>

              {/* Location Details (Optional) */}
              <div>
                <FormLabel>Location Details (Optional)</FormLabel>
                <div className="relative mt-2">
                  <FiMapPin size={16} className="absolute left-4 top-3.5 text-blue-400 shrink-0" />
                  <textarea
                    value={locationDetails}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setLocationDetails(e.target.value);
                      }
                    }}
                    placeholder="Venue details"
                    rows={3}
                    className={`${inputClass} pl-11 resize-none`}
                  />
                </div>
                <p className="text-zinc-600 text-xs mt-1.5 text-right">
                  {locationDetails.length}/200
                </p>
              </div>
            </div>
          </div>

          {/* ── Additional Details ── */}
          <div className="space-y-4">
            <h2 className="text-white text-lg font-semibold">Additional Details</h2>

            {!hidePrivateToggle && (
              <div
                className={`flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/60 border border-white/8 ${isPublishedPrivate ? 'opacity-60' : ''}`}
              >
                <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0">
                  <FiLock size={18} className={isPrivate ? 'text-blue-400' : 'text-zinc-400'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">Private event</p>
                  <p className="text-zinc-500 text-xs">
                    {isPublishedPrivate
                      ? 'Private status cannot be changed after publishing'
                      : 'Only invited guests can buy tickets'}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={isPublishedPrivate}
                  onClick={() => {
                    setIsPrivate((prev) => {
                      return !prev;
                    });
                  }}
                  className={`w-11 h-6 rounded-full p-0.5 transition-colors ${
                    isPrivate ? 'bg-blue-500' : 'bg-zinc-700'
                  } ${isPublishedPrivate ? 'cursor-not-allowed' : ''}`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                      isPrivate ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            )}

            {/* Guest invite section (visible when private, hidden for published private) */}
            {isPrivate && !isPublishedPrivate && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-white text-sm font-medium">Invite by email or @username</p>
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.csv';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (!file) return;
                        const validTypes = [
                          'text/csv',
                          'text/plain',
                          'application/vnd.ms-excel',
                          'application/csv',
                        ];
                        const ext = file.name.split('.').pop()?.toLowerCase();
                        if (ext !== 'csv' || !validTypes.includes(file.type)) {
                          toast.error('Invalid guest list file.');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const text = ev.target?.result as string;
                          if (!text || !text.trim()) {
                            toast.error('Guest list file is empty.');
                            return;
                          }
                          const rawTokens = text
                            .split(/[\n,;]/)
                            .map((s) => s.trim())
                            .filter(Boolean);
                          const invalidCount = rawTokens.filter(
                            (s) => !isValidEmailFormat(s),
                          ).length;
                          let emails = rawTokens.filter((s) => isValidEmailFormat(s));
                          if (user) {
                            const originalLen = emails.length;
                            emails = emails.filter(
                              (e) =>
                                e.toLowerCase() !== user.email.toLowerCase() &&
                                e.toLowerCase() !== `@${user.username?.toLowerCase()}`,
                            );
                            if (emails.length < originalLen) {
                              toast.error(
                                'You cannot invite yourself. Removed self-invitations from import.',
                              );
                            }
                          }
                          // Dedupe within the imported batch itself (case-insensitive)
                          const seenInBatch = new Set<string>();
                          emails = emails.filter((e) => {
                            const key = e.toLowerCase();
                            if (seenInBatch.has(key)) return false;
                            seenInBatch.add(key);
                            return true;
                          });
                          setGuests((prev) => {
                            const existing = new Set(prev.map((g) => g.email.toLowerCase()));
                            const newOnes = emails.filter((e) => !existing.has(e.toLowerCase()));
                            return [...prev, ...newOnes.map((email) => ({ email }))];
                          });
                          if (invalidCount > 0) {
                            toast.error(
                              `${invalidCount} entr${invalidCount === 1 ? 'y' : 'ies'} skipped — not a valid email address.`,
                            );
                          }
                          if (emails.length === 0) {
                            toast.error('Guest list file has no valid email entries.');
                          } else {
                            toast.success(`${emails.length} guests imported`);
                          }
                        };
                        reader.onerror = () => {
                          toast.error('Invalid guest list file.');
                        };
                        reader.readAsText(file);
                      };
                      input.click();
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white transition-all text-sm"
                  >
                    <FiUpload size={14} />
                    Import list (csv)
                  </button>
                </div>
                <div className="bg-zinc-900/60 border border-white/8 rounded-2xl px-4 py-3 min-h-[120px]">
                  {guests.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {guests.map((g, i) => (
                        <span
                          key={`${g.email}-${i}`}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border ${
                            g.email.startsWith('@')
                              ? 'bg-purple-600/20 border-purple-500/40 text-purple-300'
                              : 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                          }`}
                        >
                          {g.email.startsWith('@') && <FiLock size={12} />}
                          {g.email}
                          <button
                            type="button"
                            onClick={() => setGuests((prev) => prev.filter((_, idx) => idx !== i))}
                            className="hover:text-white transition-colors"
                          >
                            <FiX size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <textarea
                    value={guestInput}
                    onChange={(e) => setGuestInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        const raw = e.key === ',' ? guestInput + ',' : guestInput;
                        const parts = raw
                          .split(/[,;\n]/)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const last = parts[parts.length - 1];
                        if (last && (last.startsWith('@') || last.includes('@'))) {
                          void (async () => {
                            const accepted = await addGuestToken(last);
                            if (accepted) setGuestInput('');
                          })();
                        }
                      }
                    }}
                    onBlur={() => {
                      const parts = guestInput
                        .split(/[,;\n]/)
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const tokens = parts.filter((p) => p.startsWith('@') || p.includes('@'));
                      if (tokens.length === 0) return;
                      void (async () => {
                        const accepted = await addGuestTokens(tokens);
                        if (accepted.length > 0) setGuestInput('');
                      })();
                    }}
                    placeholder="name@example.com or @username, separate with commas"
                    rows={3}
                    className="w-full bg-transparent border-none text-white placeholder:text-zinc-600 focus:ring-0 focus:outline-none text-[14px] resize-none"
                  />
                  {isValidatingUsernames && (
                    <p className="mt-2 text-zinc-500 text-xs flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full border-2 border-zinc-500 border-t-white animate-spin" />
                      Validating username…
                    </p>
                  )}
                </div>
                {errors.guests && <p className="text-red-400 text-xs">{errors.guests}</p>}
              </div>
            )}

            {/* Genre picker row */}
            <button
              type="button"
              onClick={() => setShowGenreModal(true)}
              className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/60 border border-white/8 hover:border-white/15 hover:bg-zinc-800/40 transition-all text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0">
                <FiMusic size={18} className="text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">
                  {genres.length > 0
                    ? `${genres.length} genre${genres.length > 1 ? 's' : ''} added`
                    : 'Add Event genre'}
                </p>
                <p className="text-zinc-500 text-xs">Choose music genres for your event</p>
              </div>
              <FiChevronRight
                size={16}
                className="text-zinc-500 group-hover:text-white transition-colors"
              />
            </button>
            {errors.genres && <p className="text-red-400 text-xs -mt-2">{errors.genres}</p>}

            {/* Ticket details row */}
            <button
              type="button"
              onClick={() => setShowTicketModal(true)}
              className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/60 border border-white/8 hover:border-white/15 hover:bg-zinc-800/40 transition-all text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0">
                <FiCalendar size={18} className="text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">
                  {tickets.length > 0
                    ? `${tickets.length} ticket type${tickets.length > 1 ? 's' : ''} added`
                    : 'Add ticket details'}
                </p>
                <p className="text-zinc-500 text-xs">Set up ticket types, prices, & availability</p>
              </div>
              <FiChevronRight
                size={16}
                className="text-zinc-500 group-hover:text-white transition-colors"
              />
            </button>
            {errors.tickets && <p className="text-red-400 text-xs -mt-2">{errors.tickets}</p>}
          </div>

          {/* ── Collaborators ── */}
          {!(mode === 'edit' && statusBadge === 'published') && (
            <div>
              <h2 className="text-white text-lg font-semibold mb-4">Collaborators</h2>
              <div className="flex flex-col gap-6">
                <CollaboratorsSelector
                  searchOnly
                  selectedIds={collaboratorIds}
                  showHeading={false}
                  onToggle={(id) =>
                    setCollaboratorIds((prev) =>
                      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                    )
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile action buttons ── */}
      <div className="flex sm:hidden flex-col gap-3 pt-8 mt-8 border-t border-white/5">
        {mode === 'create' && (
          <>
            <Button
              type="button"
              variant="ghost"
              className="w-full rounded-full h-14 border border-white/10 text-zinc-300"
              onClick={() => handleSubmit('save')}
              disabled={isLoading}
            >
              <FiCheck size={16} className="mr-2" />
              Save as Draft
            </Button>
            <Button
              type="button"
              className="w-full rounded-full h-14 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
              onClick={() => handleSubmit('publish')}
              disabled={isLoading}
            >
              <FiPlus size={16} className="mr-2" />
              Create
            </Button>
          </>
        )}
        {mode === 'edit' && statusBadge === 'draft' && (
          <>
            <Button
              type="button"
              variant="ghost"
              className="w-full rounded-full h-14 border border-white/10 text-zinc-300"
              onClick={() => handleSubmit('save')}
              disabled={isLoading || !isDirty}
            >
              <FiCheck size={16} className="mr-2" />
              Save as Draft
            </Button>
            <Button
              type="button"
              className="w-full rounded-full h-14 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
              onClick={() => handleSubmit('publish')}
              disabled={isLoading}
            >
              <FiPlus size={16} className="mr-2" />
              Publish
            </Button>
          </>
        )}
        {mode === 'edit' && statusBadge !== 'draft' && (
          <Button
            type="button"
            className="w-full rounded-full h-14 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 disabled:bg-zinc-700 disabled:text-zinc-400"
            onClick={() => handleSubmit('save')}
            disabled={isLoading || !isDirty}
          >
            <FiCheck size={16} className="mr-2" />
            Save Changes
          </Button>
        )}
        {mode === 'edit' && secondaryActionLabel && onSecondaryAction && (
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-full h-14 border-red-500/30 text-red-400"
            onClick={onSecondaryAction}
          >
            <FiX size={16} className="mr-2" />
            {secondaryActionLabel}
          </Button>
        )}
      </div>

      {/* ── Location Modal ── */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-0 sm:px-4">
          <div className="w-full sm:max-w-md bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-semibold">Select Location</h3>
              <button
                onClick={() => setShowLocationModal(false)}
                className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <LocationSearchInput
                initialLocation={locationBody}
                onLocationSelected={(loc) => {
                  setLocationBody(loc);
                  if (loc) setShowLocationModal(false);
                }}
                placeholder="Search city, address..."
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Genre Modal ── */}
      {showGenreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-0 sm:px-4">
          <div className="w-full sm:max-w-md bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-semibold">Genre</h3>
              <button
                onClick={() => {
                  setShowGenreModal(false);
                  setGenreSearch('');
                }}
                className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <FiSearch
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <input
                type="text"
                value={genreSearch}
                onChange={(e) => setGenreSearch(e.target.value)}
                placeholder="Search genres"
                className={`${inputClass} pl-11`}
              />
            </div>

            {/* Selected count + Clear */}
            <div className="flex items-center justify-between mb-3">
              <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium border border-blue-500/30">
                {genres.length} genre{genres.length !== 1 ? 's' : ''} selected
              </span>
              {genres.length > 0 && (
                <button
                  onClick={() => setGenres([])}
                  className="flex items-center gap-1 text-zinc-400 hover:text-white text-sm transition-colors"
                >
                  Clear all <FiX size={14} />
                </button>
              )}
            </div>

            {/* Genre list */}
            <div className="flex-1 overflow-y-auto space-y-0.5 min-h-0">
              {isLoadingGenres ? (
                <p className="text-zinc-500 text-sm px-1 py-6 text-center">Loading genres…</p>
              ) : filteredGenres.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-12">
                  <p className="text-zinc-400 text-sm">
                    {genreSearch ? `No genre found for "${genreSearch}"` : 'No genres available.'}
                  </p>
                  {genreSearch.trim() && (
                    <button
                      type="button"
                      onClick={handleAddGenre}
                      disabled={createGenreMutation.isPending}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600/20 border border-blue-500/30 hover:bg-blue-600/30 disabled:opacity-40 disabled:cursor-not-allowed text-blue-400 text-[13px] font-semibold transition-all"
                    >
                      {createGenreMutation.isPending ? (
                        <div className="w-3 h-3 border-2 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" />
                      ) : (
                        <FiPlus size={14} />
                      )}
                      Add "{genreSearch.trim()}"
                    </button>
                  )}
                </div>
              ) : (
                filteredGenres.map((g) => {
                  const selected = genres.includes(g);
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() =>
                        setGenres((prev) => (selected ? prev.filter((x) => x !== g) : [...prev, g]))
                      }
                      className="w-full flex items-center justify-between px-3 py-3.5 rounded-xl hover:bg-white/5 transition-colors text-left"
                    >
                      <span
                        className={`text-[15px] font-medium ${selected ? 'text-white' : 'text-zinc-300'}`}
                      >
                        {g}
                      </span>
                      {selected && (
                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                          <FiCheck size={14} className="text-white" />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {/* Continue button */}
            <div className="mt-4 pt-2">
              <Button
                onClick={() => {
                  setShowGenreModal(false);
                  setGenreSearch('');
                }}
                className="w-full rounded-2xl h-14 bg-blue-600 hover:bg-blue-500 text-white font-semibold"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ticket Modal ── */}
      {showTicketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-0 sm:px-4">
          <div className="w-full sm:max-w-lg bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">Ticket Details</h3>
              <button
                onClick={() => {
                  setShowTicketModal(false);
                  setTicketFormMode('list');
                  setEditingTicketIndex(null);
                }}
                className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
              >
                <FiX size={20} />
              </button>
            </div>

            {ticketFormMode === 'list' && (
              <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
                {tickets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-5">
                      <FiTag size={28} className="text-white" />
                    </div>
                    <p className="text-white text-lg font-semibold mb-2">No Tickets Added Yet</p>
                    <p className="text-zinc-500 text-sm mb-8 text-center">
                      Add tickets to start selling to your audience
                    </p>
                    <Button
                      type="button"
                      onClick={() => setTicketFormMode('add')}
                      className="rounded-full px-8 h-12 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 font-semibold"
                    >
                      <FiPlus size={16} className="mr-2" />
                      Add First Ticket
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 pr-1">
                    {tickets.map((t, i) => {
                      const expired = isTicketExpired(t, endDate, endTime);
                      return (
                        <button
                          key={i}
                          type="button"
                          disabled={false}
                          title={
                            expired
                              ? 'Ticket sales have ended — click to edit or reopen.'
                              : undefined
                          }
                          onClick={() => {
                            setEditingTicketIndex(i);
                            setTicketFormMode('edit');
                          }}
                          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-zinc-900/60 border border-white/8 transition-all text-left group hover:border-white/15 cursor-pointer"
                        >
                          <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0">
                            <FiTag size={18} className="text-zinc-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium">{t.ticket_type}</p>
                            <p className="text-zinc-500 text-xs">
                              ${t.price} · {t.quantity} available
                            </p>
                          </div>
                          {expired ? (
                            <span className="px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-[11px] font-medium shrink-0">
                              Expired
                            </span>
                          ) : (
                            <FiChevronDown
                              size={16}
                              className="text-zinc-500 group-hover:text-white transition-colors -rotate-90"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Tips card */}
                {tickets.length === 0 && (
                  <div className="mt-6 p-5 rounded-2xl bg-zinc-900/40 border border-white/8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                        <FiInfo size={16} className="text-zinc-400" />
                      </div>
                      <p className="text-white text-sm font-semibold">Ticket Configuration Tips</p>
                    </div>
                    <ul className="space-y-2 text-zinc-500 text-sm">
                      <li>Create up to 10 different ticket types</li>
                      <li>Set realistic prices and quantities</li>
                      <li>Add clear descriptions for each ticket type</li>
                    </ul>
                  </div>
                )}

                {tickets.length > 0 && tickets.length < MAX_TICKETS && (
                  <div className="mt-4 pt-2">
                    <Button
                      type="button"
                      onClick={() => setTicketFormMode('add')}
                      className="w-full rounded-2xl h-14 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 font-semibold"
                    >
                      <FiPlus size={16} className="mr-2" />
                      Add Another Ticket
                    </Button>
                  </div>
                )}
                {tickets.length >= MAX_TICKETS && (
                  <div className="mt-4 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                    <p className="text-amber-400 text-sm text-center">
                      Maximum of 10 ticket types allowed.
                    </p>
                  </div>
                )}
              </div>
            )}

            {(ticketFormMode === 'add' || ticketFormMode === 'edit') && (
              <TicketForm
                mode={ticketFormMode}
                eventStartDate={startDate}
                eventStartTime={startTime}
                eventEndDate={endDate}
                eventEndTime={endTime}
                initialTicket={
                  ticketFormMode === 'edit' && editingTicketIndex !== null
                    ? tickets[editingTicketIndex]
                    : undefined
                }
                isExpired={
                  ticketFormMode === 'edit' && editingTicketIndex !== null
                    ? isTicketExpired(tickets[editingTicketIndex], endDate, endTime)
                    : false
                }
                onSave={(ticket) => {
                  if (ticketFormMode === 'edit' && editingTicketIndex !== null) {
                    setTickets((prev) =>
                      prev.map((t, i) => (i === editingTicketIndex ? { ...ticket, id: t.id } : t)),
                    );
                  } else {
                    setTickets((prev) => [...prev, ticket]);
                  }
                  setTicketFormMode('list');
                  setEditingTicketIndex(null);
                }}
                onCancel={() => {
                  setTicketFormMode('list');
                  setEditingTicketIndex(null);
                }}
                onDelete={
                  ticketFormMode === 'edit' && editingTicketIndex !== null
                    ? () => {
                        setTickets((prev) => prev.filter((_, i) => i !== editingTicketIndex));
                        setTicketFormMode('list');
                        setEditingTicketIndex(null);
                      }
                    : undefined
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
